import React, { useState } from 'react';
import { Box, Button, Typography, Alert, Card, CardContent, CircularProgress, Stack } from '@mui/joy';
import { migrateCustomersFromQuoteDock } from '../utils/customerMigration';
import { verifyCustomerMigration } from '../utils/verifyMigration';
import { fixProjectCustomerReferences } from '../utils/fixProjectCustomerReferences';
import { diagnoseProjectCustomers } from '../utils/diagnoseProjectCustomers';
import { detailedProjectVerification } from '../utils/detailedProjectVerification';
import { rebuildMigrationMappings } from '../utils/rebuildMigrationMappings';
import { compareProjectMappingIds } from '../utils/compareProjectMappingIds';
import { directProjectUpdate } from '../utils/directProjectUpdate';
import { updateDocumentCustomerTitles } from '../utils/updateDocumentCustomerTitles';
import { fixOrphanedDocuments } from '../utils/fixOrphanedDocuments';

const MigrationPage: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState<any>(null);
  const [isFixingProjects, setIsFixingProjects] = useState(false);
  const [projectFixResults, setProjectFixResults] = useState<any>(null);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [diagnosisResults, setDiagnosisResults] = useState<any>(null);
  const [isDetailedVerifying, setIsDetailedVerifying] = useState(false);
  const [detailedResults, setDetailedResults] = useState<any>(null);
  const [isRebuildingMappings, setIsRebuildingMappings] = useState(false);
  const [rebuildResults, setRebuildResults] = useState<any>(null);
  const [isComparingIds, setIsComparingIds] = useState(false);
  const [compareResults, setCompareResults] = useState<any>(null);
  const [isDirectUpdating, setIsDirectUpdating] = useState(false);
  const [directUpdateResults, setDirectUpdateResults] = useState<any>(null);
  const [isUpdatingTitles, setIsUpdatingTitles] = useState(false);
  const [titleUpdateResults, setTitleUpdateResults] = useState<any>(null);
  const [isFixingOrphans, setIsFixingOrphans] = useState(false);
  const [orphanFixResults, setOrphanFixResults] = useState<any>(null);

  const handleRunMigration = async () => {
    setIsRunning(true);
    setError(null);
    setResults(null);

    try {
      const migrationResults = await migrateCustomersFromQuoteDock();
      setResults(migrationResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Migration failed');
    } finally {
      setIsRunning(false);
    }
  };

  const handleVerifyMigration = async () => {
    setIsVerifying(true);
    setVerificationResults(null);

    try {
      const verifyResults = await verifyCustomerMigration();
      setVerificationResults(verifyResults);
    } catch (err) {
      setVerificationResults({
        success: false,
        error: err instanceof Error ? err.message : 'Verification failed'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFixProjectReferences = async () => {
    setIsFixingProjects(true);
    setProjectFixResults(null);

    try {
      const fixResults = await fixProjectCustomerReferences();
      setProjectFixResults(fixResults);
    } catch (err) {
      setProjectFixResults({
        success: false,
        error: err instanceof Error ? err.message : 'Project fix failed'
      });
    } finally {
      setIsFixingProjects(false);
    }
  };

  const handleDiagnoseProjects = async () => {
    setIsDiagnosing(true);
    setDiagnosisResults(null);

    try {
      const diagResults = await diagnoseProjectCustomers();
      setDiagnosisResults(diagResults);
    } catch (err) {
      setDiagnosisResults({
        success: false,
        error: err instanceof Error ? err.message : 'Diagnosis failed'
      });
    } finally {
      setIsDiagnosing(false);
    }
  };

  const handleDetailedVerification = async () => {
    setIsDetailedVerifying(true);
    setDetailedResults(null);

    try {
      const detailResults = await detailedProjectVerification();
      setDetailedResults(detailResults);
    } catch (err) {
      setDetailedResults({
        success: false,
        error: err instanceof Error ? err.message : 'Detailed verification failed'
      });
    } finally {
      setIsDetailedVerifying(false);
    }
  };

  const handleRebuildMappings = async () => {
    setIsRebuildingMappings(true);
    setRebuildResults(null);

    try {
      const rebuildRes = await rebuildMigrationMappings();
      setRebuildResults(rebuildRes);
    } catch (err) {
      setRebuildResults({
        success: false,
        error: err instanceof Error ? err.message : 'Rebuild failed'
      });
    } finally {
      setIsRebuildingMappings(false);
    }
  };

  const handleCompareIds = async () => {
    setIsComparingIds(true);
    setCompareResults(null);

    try {
      const compareRes = await compareProjectMappingIds();
      setCompareResults(compareRes);
    } catch (err) {
      setCompareResults({
        success: false,
        error: err instanceof Error ? err.message : 'ID comparison failed'
      });
    } finally {
      setIsComparingIds(false);
    }
  };

  const handleDirectUpdate = async () => {
    setIsDirectUpdating(true);
    setDirectUpdateResults(null);

    try {
      const updateRes = await directProjectUpdate();
      setDirectUpdateResults(updateRes);
    } catch (err) {
      setDirectUpdateResults({
        success: false,
        error: err instanceof Error ? err.message : 'Direct update failed'
      });
    } finally {
      setIsDirectUpdating(false);
    }
  };

  const handleUpdateDocumentTitles = async () => {
    setIsUpdatingTitles(true);
    setTitleUpdateResults(null);

    try {
      const updateRes = await updateDocumentCustomerTitles();
      setTitleUpdateResults(updateRes);
    } catch (err) {
      setTitleUpdateResults({
        success: false,
        error: err instanceof Error ? err.message : 'Document title update failed'
      });
    } finally {
      setIsUpdatingTitles(false);
    }
  };

  const handleFixOrphanedDocuments = async () => {
    setIsFixingOrphans(true);
    setOrphanFixResults(null);

    try {
      const fixRes = await fixOrphanedDocuments();
      setOrphanFixResults(fixRes);
    } catch (err) {
      setOrphanFixResults({
        success: false,
        error: err instanceof Error ? err.message : 'Orphaned document fix failed'
      });
    } finally {
      setIsFixingOrphans(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography level="h2" sx={{ mb: 3 }}>
        Customer Migration from QuoteDock
      </Typography>

      <Alert color="warning" sx={{ mb: 3 }}>
        <Typography level="title-sm">⚠️ Important</Typography>
        <Typography level="body-sm">
          This will migrate all QuoteDock customers to your JARVIS database and update project relationships. 
          Make sure you have a database backup before proceeding.
        </Typography>
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>Migration Process</Typography>
          <Typography level="body-sm" sx={{ mb: 2 }}>
            This migration will:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>Fetch all customers from QuoteDock API</li>
            <li>Create them in your JARVIS customers table</li>
            <li>Create mapping records (QuoteDock ID → JARVIS UUID)</li>
            <li>Update all project.customer_id references</li>
          </ul>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={handleRunMigration}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings}
          size="md"
          color="primary"
          startDecorator={isRunning ? <CircularProgress size="sm" /> : null}
        >
          {isRunning ? 'Running Migration...' : 'Start Migration'}
        </Button>
        
        <Button
          onClick={handleVerifyMigration}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings}
          size="md"
          color="success"
          variant="outlined"
          startDecorator={isVerifying ? <CircularProgress size="sm" /> : null}
        >
          {isVerifying ? 'Verifying...' : 'Verify Migration'}
        </Button>
        
        <Button
          onClick={handleRebuildMappings}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings}
          size="md"
          color="danger"
          variant="outlined"
          startDecorator={isRebuildingMappings ? <CircularProgress size="sm" /> : null}
        >
          {isRebuildingMappings ? 'Rebuilding...' : 'Rebuild Mappings'}
        </Button>
        
        <Button
          onClick={handleDiagnoseProjects}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings}
          size="md"
          color="neutral"
          variant="outlined"
          startDecorator={isDiagnosing ? <CircularProgress size="sm" /> : null}
        >
          {isDiagnosing ? 'Diagnosing...' : 'Diagnose Projects'}
        </Button>
        
        <Button
          onClick={handleDetailedVerification}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings || isComparingIds}
          size="md"
          color="neutral"
          variant="soft"
          startDecorator={isDetailedVerifying ? <CircularProgress size="sm" /> : null}
        >
          {isDetailedVerifying ? 'Analyzing...' : 'Detailed Analysis'}
        </Button>
        
        <Button
          onClick={handleCompareIds}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings || isComparingIds}
          size="md"
          color="neutral"
          variant="outlined"
          startDecorator={isComparingIds ? <CircularProgress size="sm" /> : null}
        >
          {isComparingIds ? 'Comparing...' : 'Compare IDs'}
        </Button>
        
        <Button
          onClick={handleDirectUpdate}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings || isComparingIds || isDirectUpdating}
          size="md"
          color="success"
          variant="solid"
          startDecorator={isDirectUpdating ? <CircularProgress size="sm" /> : null}
        >
          {isDirectUpdating ? 'Updating...' : 'Direct Update'}
        </Button>
        
        <Button
          onClick={handleFixProjectReferences}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings || isComparingIds || isDirectUpdating || isUpdatingTitles}
          size="md"
          color="warning"
          variant="outlined"
          startDecorator={isFixingProjects ? <CircularProgress size="sm" /> : null}
        >
          {isFixingProjects ? 'Fixing...' : 'Fix Project Links'}
        </Button>
        
        <Button
          onClick={handleUpdateDocumentTitles}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings || isComparingIds || isDirectUpdating || isUpdatingTitles || isFixingOrphans}
          size="md"
          color="primary"
          variant="soft"
          startDecorator={isUpdatingTitles ? <CircularProgress size="sm" /> : null}
        >
          {isUpdatingTitles ? 'Updating...' : 'Update Document Titles'}
        </Button>
        
        <Button
          onClick={handleFixOrphanedDocuments}
          disabled={isRunning || isVerifying || isFixingProjects || isDiagnosing || isDetailedVerifying || isRebuildingMappings || isComparingIds || isDirectUpdating || isUpdatingTitles || isFixingOrphans}
          size="md"
          color="success"
          variant="outlined"
          startDecorator={isFixingOrphans ? <CircularProgress size="sm" /> : null}
        >
          {isFixingOrphans ? 'Fixing...' : 'Fix Orphaned Documents'}
        </Button>
      </Stack>

      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          <Typography level="title-sm">Migration Failed</Typography>
          <Typography level="body-sm">{error}</Typography>
        </Alert>
      )}

      {results && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Migration Results
            </Typography>
            
            {results.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Migration completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Migration failed: {results.error}
              </Alert>
            )}

            {results.success && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Summary:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Total customers: {results.total}</li>
                  <li>Successfully migrated: {results.successful}</li>
                  <li>Failed: {results.failed}</li>
                </ul>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {verificationResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Verification Results
            </Typography>
            
            {verificationResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Migration verification completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Verification failed: {verificationResults.error}
              </Alert>
            )}

            {verificationResults.success && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Verification Summary:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Migration mappings found: {verificationResults.mappingCount}</li>
                  <li>Total customers in JARVIS database: {verificationResults.customerCount}</li>
                  <li>Projects with customer relationships: {verificationResults.projectsWithCustomers}</li>
                </ul>
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed verification logs.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {diagnosisResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Project Diagnosis Results
            </Typography>
            
            {diagnosisResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Project diagnosis completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Diagnosis failed: {diagnosisResults.error}
              </Alert>
            )}

            {diagnosisResults.success && diagnosisResults.analysis && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Customer ID Analysis:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Projects with no customer_id: {diagnosisResults.analysis.null}</li>
                  <li>Projects with UUID customer_id: {diagnosisResults.analysis.uuid}</li>
                  <li>Projects with integer customer_id: {diagnosisResults.analysis.integer}</li>
                  <li>Projects with string customer_id: {diagnosisResults.analysis.string}</li>
                  <li>Projects with other customer_id: {diagnosisResults.analysis.other}</li>
                </ul>
                {diagnosisResults.potentialMatches && diagnosisResults.potentialMatches.length > 0 && (
                  <>
                    <Typography level="body-sm" sx={{ mt: 2, mb: 1 }}>
                      🎯 Found {diagnosisResults.potentialMatches.length} potential matches
                    </Typography>
                  </>
                )}
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed analysis and sample data.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {detailedResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Detailed Analysis Results
            </Typography>
            
            {detailedResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Detailed analysis completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Analysis failed: {detailedResults.error}
              </Alert>
            )}

            {detailedResults.success && detailedResults.analysis && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Detailed Project Analysis:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Total projects: {detailedResults.analysis.total}</li>
                  <li>Projects with UUID customer_id: {detailedResults.analysis.uuid}</li>
                  <li>Projects with integer customer_id: {detailedResults.analysis.integer}</li>
                  <li>Migration mappings: {detailedResults.mappingCount}</li>
                  <li>Project-mapping matches found: {detailedResults.matchCount}</li>
                </ul>
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed project data, mappings, and potential matches.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {rebuildResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Mapping Rebuild Results
            </Typography>
            
            {rebuildResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Mapping rebuild completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Rebuild failed: {rebuildResults.error}
              </Alert>
            )}

            {rebuildResults.success && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Rebuild Summary:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>JARVIS customers: {rebuildResults.jarvisCustomers}</li>
                  <li>QuoteDock customers: {rebuildResults.quoteDockCustomers}</li>
                  <li>Existing mappings: {rebuildResults.existingMappings}</li>
                  <li>New matches found: {rebuildResults.newMatches}</li>
                  <li>New mappings created: {rebuildResults.newMappingsCreated}</li>
                  <li>Final total mappings: {rebuildResults.finalMappingCount}</li>
                </ul>
                {rebuildResults.matchStrategies && (
                  <>
                    <Typography level="body-sm" sx={{ mt: 2, mb: 1 }}>
                      🎯 Matching strategies used:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {Object.entries(rebuildResults.matchStrategies).map(([strategy, count]) => (
                        Number(count) > 0 && <li key={strategy}>{strategy}: {Number(count)} matches</li>
                      ))}
                    </ul>
                  </>
                )}
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed rebuild logs and match details.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {compareResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              ID Comparison Results
            </Typography>
            
            {compareResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ ID comparison completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Comparison failed: {compareResults.error}
              </Alert>
            )}

            {compareResults.success && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 ID Comparison Summary:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Projects analyzed: {compareResults.projectCount}</li>
                  <li>Unique project customer IDs: {compareResults.uniqueProjectIds}</li>
                  <li>Migration mappings: {compareResults.mappingCount}</li>
                  <li>🎯 Matches found: {compareResults.matches}</li>
                  <li>❌ Project IDs not in mappings: {compareResults.projectOnlyIds}</li>
                  <li>ℹ️ Mapping IDs not used by projects: {compareResults.mappingOnlyIds}</li>
                </ul>
                {compareResults.matches === 0 && (
                  <Alert color="warning" sx={{ mt: 2 }}>
                    <Typography level="body-sm">
                      ⚠️ No matches found! This explains why project updates failed. 
                      The customer IDs in your projects don't match any QuoteDock IDs in the migration mappings.
                    </Typography>
                  </Alert>
                )}
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed ID lists and sample data.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {directUpdateResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Direct Update Results
            </Typography>
            
            {directUpdateResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Direct update completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Update failed: {directUpdateResults.error}
              </Alert>
            )}

            {directUpdateResults.success && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Update Summary:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Total projects processed: {directUpdateResults.totalProjects}</li>
                  <li>✅ Successfully updated: {directUpdateResults.updated}</li>
                  <li>❌ Failed: {directUpdateResults.failed}</li>
                  <li>⚠️ Unmapped (set to NULL): {directUpdateResults.unmapped}</li>
                  <li>⏭️ Already UUID: {directUpdateResults.skipped}</li>
                </ul>
                {directUpdateResults.updated > 0 && (
                  <Alert color="success" sx={{ mt: 2 }}>
                    <Typography level="body-sm">
                      🎉 {directUpdateResults.updated} projects now have proper JARVIS customer UUIDs!
                    </Typography>
                  </Alert>
                )}
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed update logs and results.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {titleUpdateResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Document Title Update Results
            </Typography>
            
            {titleUpdateResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Document title update completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Update failed: {titleUpdateResults.error}
              </Alert>
            )}

            {titleUpdateResults.success && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Update Summary:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Total documents processed: {titleUpdateResults.totalDocuments}</li>
                  <li>✅ Successfully updated: {titleUpdateResults.updated}</li>
                  <li>⚠️ Customer not found: {titleUpdateResults.customerNotFound}</li>
                  <li>⏭️ No title to update: {titleUpdateResults.noTitleToUpdate}</li>
                  <li>❌ Failed: {titleUpdateResults.failed}</li>
                </ul>
                {titleUpdateResults.updated > 0 && (
                  <Alert color="success" sx={{ mt: 2 }}>
                    <Typography level="body-sm">
                      🎉 {titleUpdateResults.updated} documents now have customer titles!
                    </Typography>
                  </Alert>
                )}
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed update logs.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {orphanFixResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Orphaned Document Fix Results
            </Typography>
            
            {orphanFixResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Orphaned document fix completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Fix failed: {orphanFixResults.error}
              </Alert>
            )}

            {orphanFixResults.success && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Fix Summary:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Total orphaned documents: {orphanFixResults.totalDocuments}</li>
                  <li>✅ Successfully linked: {orphanFixResults.fixed}</li>
                  <li>⚠️ No customer match: {orphanFixResults.noMatch}</li>
                  <li>❌ Failed: {orphanFixResults.failed}</li>
                </ul>
                {orphanFixResults.fixed > 0 && (
                  <Alert color="success" sx={{ mt: 2 }}>
                    <Typography level="body-sm">
                      🎉 {orphanFixResults.fixed} documents now linked to customers!
                    </Typography>
                  </Alert>
                )}
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed fix logs.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {projectFixResults && (
        <Card>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Project Reference Fix Results
            </Typography>
            
            {projectFixResults.success ? (
              <Alert color="success" sx={{ mb: 2 }}>
                ✅ Project reference fix completed successfully!
              </Alert>
            ) : (
              <Alert color="danger" sx={{ mb: 2 }}>
                ❌ Project fix failed: {projectFixResults.error}
              </Alert>
            )}

            {projectFixResults.success && (
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  📊 Project Fix Summary:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Customer mappings processed: {projectFixResults.totalMappings}</li>
                  <li>Projects updated: {projectFixResults.projectsUpdated}</li>
                  {projectFixResults.methodCounts && Object.entries(projectFixResults.methodCounts).map(([method, count]) => {
                    const countNum = Number(count);
                    return countNum > 0 ? <li key={method}>Updated by {method}: {countNum} projects</li> : null;
                  })}
                </ul>
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  💡 Check the browser console (F12) for detailed fix logs.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MigrationPage;
